/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import {
  DICOM_TAG_FORMAT,
  DYNAMIC_DURATION_PATTERN,
  DYNAMIC_PERIOD_PATTERN,
  DYNAMIC_FORMAT_DATE_AND_TIME,
  HL7_FIELD_FORMAT,
  DATE_RANGE,
  TIME,
  TIME_RANGE,
  DATE_AND_TIME,
  DATE_AND_TIME_INTERVAL,
  TIME_INTERVAL,
  DATE_INTERVAL,
  DATE,
  DATE_AND_TIME_RANGE,
  BETWEEN_DATE_AND_TIME_INTERVAL,
  BETWEEN_DATE_INTERVAL,
  BETWEEN_TIME_INTERVAL,
  CONSTANT,
  REGEX,
  REGEX_PATH,
  COPY,
  DYNAMIC,
  SCRIPT,
  REMOVE,
  DICOM,
  EQUAL,
  GREATER,
  GREATER_OR_EQUAL,
  LESS,
  LESS_OR_EQUAL,
  LIKE,
  NOT_EQUAL,
  NOT_LIKE,
  IN_RANGE,
  OUT_OF_RANGE,
} from "../../constant";
import {
  Anonymize,
  ConditionError,
  TransformObjectData,
  TransformTypeEnum,
  UntaggedConditionObjectData,
} from "@advapacs/common";
import { checkValidatePeriod } from "./helpers";

class Validator {
  errors: ConditionError[] = [];

  static invalidDateVal(value: TransformObjectData): boolean {
    // It's Period P2D,P3Y3M2D
    const { dynamic } = value;
    if (dynamic) {
      const isPeriod = !Validator.validateDynamicPeriod(dynamic?.value);
      return (
        dynamic?.operator &&
        dynamic?.value &&
        dynamic?.operator === DATE &&
        isPeriod
      );
    }
    return false;
  }

  static invalidDateRangeFrom(value: TransformObjectData): boolean {
    // It's Period P2D,P3Y3M2D
    const { dynamic } = value;
    if (dynamic) {
      const isPeriod = !Validator.validateDynamicPeriod(dynamic?.from);
      return (
        dynamic?.operator &&
        dynamic?.from &&
        dynamic?.operator === DATE_RANGE &&
        isPeriod
      );
    }
    return false;
  }

  static invalidDateRangeTo(value: TransformObjectData): boolean {
    // It's Period P2D,P3Y3M2D
    const { dynamic } = value;
    if (dynamic) {
      const isPeriod = !Validator.validateDynamicPeriod(dynamic?.to);
      return (
        dynamic?.operator &&
        dynamic?.to &&
        dynamic?.operator === DATE_RANGE &&
        isPeriod
      );
    }
    return false;
  }

  static invalidTimeVal(value: TransformObjectData): boolean {
    /// It's Duration PT13M,PT2H1M
    const { dynamic } = value;
    if (dynamic) {
      const isValid = !Validator.validateDynamicDuration(dynamic?.value); // show invalid if it's false
      return (
        dynamic?.operator &&
        dynamic?.value &&
        dynamic?.operator === TIME &&
        isValid
      );
    }
    return false;
  }

  static invalidTimeRangeFrom(value: TransformObjectData): boolean {
    /// It's Duration PT13M,PT2H1M
    const { dynamic } = value;
    if (dynamic) {
      const isValidTimeRangeFrom = !Validator.validateDynamicDuration(
        dynamic?.from
      ); // show invalid if it's false
      return (
        dynamic?.operator &&
        dynamic?.from &&
        dynamic?.operator === TIME_RANGE &&
        isValidTimeRangeFrom
      );
    }
    return false;
  }

  static invalidTimeRangeTo(value: TransformObjectData): boolean {
    /// It's Duration PT13M,PT2H1M
    const { dynamic } = value;
    if (dynamic) {
      const isValidTimeRangeTo = !Validator.validateDynamicDuration(
        dynamic?.to
      ); // show invalid if it's false
      return (
        dynamic?.operator &&
        dynamic?.to &&
        dynamic?.operator === TIME_RANGE &&
        isValidTimeRangeTo
      );
    }
    return false;
  }

  static invalidDateAndTimeVal(value: TransformObjectData): boolean {
    // if value of dynamic in ["Y", "M", "W", "D"] and without T => it's check Period
    const { dynamic } = value;
    if (dynamic) {
      const isValidPeriod = !Validator.validateDynamicPeriod(dynamic?.value);
      const isValidDuration = !Validator.validateDynamicDuration(
        dynamic?.value
      );
      const isPeriod = dynamic?.value && checkValidatePeriod(dynamic?.to);
      if (isPeriod) {
        return (
          dynamic?.operator &&
          dynamic?.value &&
          dynamic?.operator === DATE_AND_TIME &&
          isValidPeriod
        );
      } else {
        return (
          dynamic?.operator &&
          dynamic?.value &&
          dynamic?.operator === DATE_AND_TIME &&
          isValidDuration
        );
      }
    }
    return false;
  }

  static invalidDateAndTimeRangeFrom(value: TransformObjectData): boolean {
    // if value of dynamic in ["Y", "M", "W", "D"] and without T => it's check Period
    const { dynamic } = value;
    if (dynamic) {
      const isValidPeriod = !Validator.validateDynamicPeriod(dynamic?.from);
      const isValidDuration = !Validator.validateDynamicDuration(dynamic?.from);
      const isPeriod = dynamic?.from && checkValidatePeriod(dynamic?.from);
      if (isPeriod) {
        return (
          dynamic?.operator &&
          dynamic?.from &&
          dynamic?.operator === DATE_AND_TIME_RANGE &&
          isValidPeriod
        );
      } else {
        return (
          dynamic?.operator &&
          dynamic?.from &&
          dynamic?.operator === DATE_AND_TIME_RANGE &&
          isValidDuration
        );
      }
    }
    return false;
  }

  static invalidDateAndTimeRangeTo(value: TransformObjectData): boolean {
    // if value of dynamic in ["Y", "M", "W", "D"] and without T => it's check Period
    const { dynamic } = value;
    if (dynamic) {
      const isValidPeriod = !Validator.validateDynamicPeriod(dynamic?.to);
      const isValidDuration = !Validator.validateDynamicDuration(dynamic?.to);
      const isPeriod = dynamic?.to && checkValidatePeriod(dynamic?.to);
      if (isPeriod) {
        return (
          dynamic?.operator &&
          dynamic?.to &&
          dynamic?.operator === DATE_AND_TIME_RANGE &&
          isValidPeriod
        );
      } else {
        return (
          dynamic?.operator &&
          dynamic?.to &&
          dynamic?.operator === DATE_AND_TIME_RANGE &&
          isValidDuration
        );
      }
    }
    return false;
  }

  static invalidDynamicTag(
    value: TransformObjectData,
    transformType: TransformTypeEnum
  ): boolean {
    const { dynamic } = value;
    if (dynamic) {
      return (
        !dynamic?.tag || !Validator.validateTag(dynamic?.tag, transformType)
      );
    }
    return false;
  }

  static invalidDynamicDateTimeValue(value: TransformObjectData): boolean {
    const { dynamic } = value;
    if (dynamic) {
      return (
        [
          DATE,
          TIME,
          DATE_AND_TIME,
          DATE_INTERVAL,
          TIME_INTERVAL,
          DATE_AND_TIME_INTERVAL,
        ].includes(dynamic?.operator) && !dynamic?.value
      );
    }
    return false;
  }

  static invalidDynamicDateTimeRangeValue(value: TransformObjectData): boolean {
    const { dynamic } = value;
    if (dynamic) {
      return (
        [
          DATE_AND_TIME_RANGE,
          TIME_RANGE,
          DATE_RANGE,
          BETWEEN_DATE_AND_TIME_INTERVAL,
          BETWEEN_DATE_INTERVAL,
          BETWEEN_TIME_INTERVAL,
        ].includes(dynamic?.operator) &&
        !dynamic?.from &&
        !dynamic?.to
      );
    }
    return false;
  }

  // Condition Rule Validate

  static isValidDateIntervalVal(value: UntaggedConditionObjectData): boolean {
    // It's Period P2D,P3Y3M2D
    const { dynamic } = value;
    if (dynamic) {
      const isPeriod = !Validator.validateDynamicPeriod(dynamic?.value);
      return (
        dynamic?.value &&
        dynamic?.operator &&
        dynamic?.operator === DATE_INTERVAL &&
        isPeriod
      );
    }
    return false;
  }

  static isValidTimeIntervalVal(value: UntaggedConditionObjectData): boolean {
    // It's Duration PT13M,PT2H1M
    const { dynamic } = value;
    if (dynamic) {
      const isDuration = Validator.validateDynamicPeriod(dynamic?.value); // show invalid if it's false
      return (
        dynamic?.value &&
        dynamic?.operator &&
        dynamic?.operator === TIME_INTERVAL &&
        isDuration
      );
    }
    return false;
  }

  static isValidDateAndTimeIntervalVal(
    value: UntaggedConditionObjectData
  ): boolean {
    const { dynamic } = value;
    if (dynamic) {
      const isPeriod = dynamic?.value && checkValidatePeriod(dynamic?.value);
      if (isPeriod) {
        return (
          dynamic?.value &&
          dynamic?.operator &&
          dynamic?.operator === DATE_AND_TIME_INTERVAL &&
          !Validator.validateDynamicPeriod(dynamic?.value)
        );
      } else {
        return (
          dynamic?.value &&
          dynamic?.operator &&
          dynamic?.operator === DATE_AND_TIME_INTERVAL &&
          !Validator.validateDynamicDuration(dynamic?.value)
        );
      }
    }
    return false;
  }

  static isValidBetweenDateIntervalFrom(
    value: UntaggedConditionObjectData
  ): boolean {
    // It's Period P2D,P3Y3M2D
    const { dynamic } = value;
    if (dynamic) {
      const isValidPeriod = !Validator.validateDynamicPeriod(dynamic?.from);
      return (
        dynamic?.from &&
        dynamic?.operator &&
        dynamic?.operator === BETWEEN_DATE_INTERVAL &&
        isValidPeriod
      );
    }
    return false;
  }

  static isValidBetweenTimeIntervalFrom(
    value: UntaggedConditionObjectData
  ): boolean {
    // It's Duration PT13M,PT2H1M
    const { dynamic } = value;
    if (dynamic) {
      const isValidDuration = Validator.validateDynamicPeriod(dynamic?.from);
      return (
        dynamic?.from &&
        dynamic?.operator &&
        dynamic?.operator === BETWEEN_TIME_INTERVAL &&
        isValidDuration
      );
    }
    return false;
  }

  static isValidBetweenDateAndTimeIntervalFrom(
    value: UntaggedConditionObjectData
  ): boolean {
    const { dynamic } = value;
    if (dynamic) {
      const isPeriod = dynamic?.from && checkValidatePeriod(dynamic?.from);
      if (isPeriod) {
        return (
          dynamic?.from &&
          dynamic?.operator &&
          dynamic?.operator === BETWEEN_DATE_AND_TIME_INTERVAL &&
          !Validator.validateDynamicPeriod(dynamic?.from)
        );
      } else {
        return (
          dynamic?.from &&
          dynamic?.operator &&
          dynamic?.operator === BETWEEN_DATE_AND_TIME_INTERVAL &&
          !Validator.validateDynamicDuration(dynamic?.from)
        );
      }
    }
    return false;
  }

  static isValidBetweenDateIntervalTo(
    value: UntaggedConditionObjectData
  ): boolean {
    // It's Period P2D,P3Y3M2D
    const { dynamic } = value;
    if (dynamic) {
      const isValidPeriodTo = !Validator.validateDynamicPeriod(dynamic?.to);
      return (
        dynamic?.to &&
        dynamic?.operator &&
        dynamic?.operator === BETWEEN_DATE_INTERVAL &&
        isValidPeriodTo
      );
    }
    return false;
  }

  static isValidBetweenTimeIntervalTo(
    value: UntaggedConditionObjectData
  ): boolean {
    // It's Duration PT13M,PT2H1M
    const { dynamic } = value;
    if (dynamic) {
      const isValidDurationTo = Validator.validateDynamicPeriod(dynamic?.to);
      return (
        dynamic?.to &&
        dynamic?.operator &&
        dynamic?.operator === BETWEEN_TIME_INTERVAL &&
        isValidDurationTo
      );
    }
    return false;
  }

  static isValidBetweenDateAndTimeIntervalTo(
    value: UntaggedConditionObjectData
  ): boolean {
    // if value of dynamic in ["Y", "M", "W", "D"] and without T => it's check Period
    const { dynamic } = value;
    if (dynamic) {
      const isPeriod = dynamic?.to && checkValidatePeriod(dynamic?.to);
      if (isPeriod) {
        return (
          dynamic?.to &&
          dynamic?.operator &&
          dynamic?.operator === BETWEEN_DATE_AND_TIME_INTERVAL &&
          !Validator.validateDynamicPeriod(dynamic?.to)
        );
      } else {
        return (
          dynamic?.to &&
          dynamic?.operator &&
          dynamic?.operator === BETWEEN_DATE_AND_TIME_INTERVAL &&
          !Validator.validateDynamicDuration(dynamic?.to)
        );
      }
    }
    return false;
  }

  static validateCondition(
    value: TransformObjectData,
    transformType: TransformTypeEnum
  ): boolean {
    const { type, transform, dynamic, dicom } = value;
    if (!type) {
      return false;
    }

    switch (type) {
      case DICOM:
        return !(
          !dicom?.tag ||
          !Validator.validateTag(dicom?.tag, transformType) ||
          !dicom?.operator ||
          ([
            EQUAL,
            GREATER,
            GREATER_OR_EQUAL,
            LESS,
            LESS_OR_EQUAL,
            LIKE,
            NOT_EQUAL,
            NOT_LIKE,
          ].includes(dicom?.operator) &&
            !dicom?.value) ||
          ([IN_RANGE, OUT_OF_RANGE].includes(dicom?.operator) &&
            (!dicom?.from || !dicom?.to))
        );

      case CONSTANT:
        return !(
          !transform?.tag ||
          !Validator.validateTag(transform?.tag, transformType)
        );

      case REGEX:
        return !(
          !transform?.tag ||
          !Validator.validateTag(transform?.tag, transformType) ||
          !transform?.regex
        );

      case REGEX_PATH:
        return !(
          !transform?.regex ||
          !Validator.validateExpressionRegex(transform?.regex)
        );

      case COPY:
        return !(
          !transform?.source ||
          !Validator.validateTag(transform?.source, transformType) ||
          !transform?.destination ||
          !Validator.validateTag(transform?.destination, transformType)
        );

      case REMOVE:
        return !(
          !transform?.tag ||
          !Validator.validateTag(transform?.tag, transformType)
        );

      case SCRIPT:
        return !(
          !transform?.script ||
          (transform?.annotations &&
            transform?.annotations.length > 0 &&
            transform?.annotations.filter((ann) => ann.type === "error")
              .length > 0)
        );

      case DYNAMIC:
        return !(
          Validator.invalidDynamicTag(value, transformType) ||
          !dynamic?.operator ||
          Validator.invalidDynamicDateTimeValue(value) ||
          Validator.invalidDynamicDateTimeRangeValue(value) ||
          Validator.invalidDateVal(value) ||
          Validator.invalidTimeVal(value) ||
          Validator.invalidDateAndTimeVal(value) ||
          Validator.invalidDateRangeFrom(value) ||
          Validator.invalidDateRangeTo(value) ||
          Validator.invalidTimeRangeFrom(value) ||
          Validator.invalidTimeRangeTo(value) ||
          Validator.invalidDateAndTimeRangeFrom(value) ||
          Validator.invalidDateAndTimeRangeTo(value) ||
          // interval time
          Validator.isValidDateIntervalVal(value) ||
          Validator.isValidTimeIntervalVal(value) ||
          Validator.isValidDateAndTimeIntervalVal(value) ||
          Validator.isValidBetweenDateIntervalFrom(value) ||
          Validator.isValidBetweenTimeIntervalFrom(value) ||
          Validator.isValidBetweenDateAndTimeIntervalFrom(value) ||
          Validator.isValidBetweenDateIntervalTo(value) ||
          Validator.isValidBetweenTimeIntervalTo(value) ||
          Validator.isValidBetweenDateAndTimeIntervalTo(value)
        );
      default:
        return true;
    }
  }

  static validateAnonymize(item: Anonymize): boolean {
    if (item) {
      return (
        item.retainDeviceIdentityOption ||
        item.retainInstitutionIdentityOption ||
        item.retainLongitudinalTemporalInformationFullDatesOption ||
        item.retainUidsOption ||
        item.retainDescriptionsOption ||
        item.retainBurnedInAnnotationsOption
      );
    }

    return false;
  }

  static validateExpressionRegex(expression: string): boolean {
    if (!expression) return true; // leave empty validator handle it
    try {
      new RegExp(expression);
      return true;
    } catch (e) {
      return false;
    }
  }

  static validateTag(tag: string, transformType: TransformTypeEnum): boolean {
    if (tag) {
      const validatePattern =
        transformType === TransformTypeEnum.HL7
          ? HL7_FIELD_FORMAT
          : DICOM_TAG_FORMAT;
      const results: boolean[] = [];
      tag
        .trim()
        .split(".")
        .forEach((comp) => {
          results.push(validatePattern.test(comp));
        });
      return !results.includes(false);
    }

    return false;
  }

  static validateDynamicDuration(dynamic: string): boolean {
    if (dynamic) {
      const results: boolean[] = [];
      dynamic
        .trim()
        .split(".")
        .forEach((comp) => {
          if (comp) {
            results.push(DYNAMIC_DURATION_PATTERN.test(comp));
          }
        });
      return !results.includes(false);
    }
    return false;
  }

  static validateBothDurationAndPeriod(dynamic: string): boolean {
    const regex = DYNAMIC_FORMAT_DATE_AND_TIME;
    if (dynamic) {
      const results: boolean[] = [];
      dynamic
        .trim()
        .split(".")
        .forEach((comp) => {
          if (comp) {
            results.push(regex.test(comp));
          }
        });
      return !results.includes(false);
    }
    return false;
  }

  static validateDynamicPeriod(dynamic: string): boolean {
    const regex = DYNAMIC_PERIOD_PATTERN;
    if (dynamic) {
      const results: boolean[] = [];
      dynamic
        .trim()
        .split(".")
        .forEach((comp) => {
          if (comp) {
            results.push(regex.test(comp));
          }
        });
      return !results.includes(false);
    }
    return false;
  }

  validate(
    rules: TransformObjectData[],
    transformType: TransformTypeEnum
  ): ConditionError[] {
    rules.forEach((item) => {
      const conditionValid = Validator.validateCondition(item, transformType);
      if (!conditionValid) {
        const { type, id } = item;
        this.errors.push({
          id,
          type,
        });
      }
    });

    return this.errors;
  }
}

export default Validator;
